import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { CONFIG } from '@cpd-app/configuration';
import {
  GetSessionResponse,
  RefreshSessionResponse,
  UserProfile
} from '@cpd-app/core-module/models';
import {
  GetReturnSessionGQL,
  GetSessionGQL,
  GetSessionQuery,
  LoginByEmailGQL,
  LoginGQL,
  Profile,
  RefreshSessionGQL,
  ReturnSessionGQL
} from '@cpd-app/generated';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { readGraphQlResult } from './apollo-result.util';

@Injectable({
  providedIn: 'root'
})
export class AuthDataService {
  constructor(
    private getSessionGQL: GetSessionGQL,
    private refreshSessionGQL: RefreshSessionGQL,
    private returnSessionGQL: ReturnSessionGQL,
    private getReturnSessionGQL: GetReturnSessionGQL,
    private loginGQL: LoginGQL,
    private loginByEmailGQL: LoginByEmailGQL
  ) {}

  public getSession(token?: string): Observable<GetSessionResponse> {
    return this.getSessionGQL.fetch({ token: token }).pipe(
      map((results: ApolloQueryResult<GetSessionQuery>) => {
        /*
         * A REJECTED TOKEN IS A FAILURE, NOT AN EMPTY SESSION.
         *
         * This used to read straight through with optional chaining. Under
         * `errorPolicy: 'all'` a denial resolves with `data: null`, so every
         * field came back undefined and the app got a tidy, error-free session
         * carrying no token at all. `getSession$` never reached its catchError,
         * so the Cognito recovery it exists to trigger never ran; instead the
         * session was marked initialised and the first real query's 403 spent a
         * refresh nobody asked for. That is the stray `refreshSession` on
         * opening a second tab (CPD-652).
         */
        const session = readGraphQlResult('getSession', results, 'getSession');

        const sessionToken: string = session.token;
        const refreshToken: string = session.refreshToken;
        const profile: Profile = session.profile;
        const isAuthenticated = !!session.isAuthenticated;

        const sessionState: GetSessionResponse = {
          token: sessionToken,
          refreshToken,
          isAuthenticated,
          userProfile: new UserProfile(profile)
        };

        return sessionState;
      })
    );
  }

  public refreshSession(
    refreshToken: string
  ): Observable<RefreshSessionResponse> {
    return this.refreshSessionGQL.fetch({ refreshToken }).pipe(
      /*
       * `results.data.refreshSession` threw `TypeError: Cannot read properties
       * of null (reading 'refreshSession')` the moment the gateway rejected the
       * token, because `errorPolicy: 'all'` resolves a denial as `data: null`.
       * That TypeError is what members saw on the 403 page, and because it
       * carries no GraphQL errors the interceptor could not recognise it as a
       * rejection and so never deleted the dead token (CPD-652).
       */
      map((results) =>
        readGraphQlResult('refreshSession', results, 'refreshSession')
      )
    );
  }

  public adminLogIn(username: string, password: string) {
    if (username.includes('@')) {
      return this.loginByEmailGQL
        .mutate({ email: username, password })
        .pipe(map((result) => result.data.loginByEmail));
    }

    return this.loginGQL
      .mutate({ username, password })
      .pipe(map((result) => result.data.login));
  }

  public createReturnSession(mailTo: string) {
    return this.returnSessionGQL.mutate({ mailTo: mailTo }).pipe(
      map((result) => {
        return result.data.returnSession.success;
      })
    );
  }

  public processReturnUrlWithToken() {
    const returnSessionToken = location.search.split('=')[1];

    return this.hydrateUserProfileFromReturnSessionToken(returnSessionToken);
  }

  public submitPasscode(passcode: string) {
    return of(passcode).pipe(map((code) => code === CONFIG.PASSCODE)); // Example passcode check
  }

  private hydrateUserProfileFromReturnSessionToken(
    returnSessionToken: string
  ): Observable<string> {
    if (!returnSessionToken || returnSessionToken.trim().length === 0) {
      // returnSessionToken missing from link params
      return of('-3');
    }
    return this.getReturnSessionGQL
      .fetch({ returnToken: returnSessionToken })
      .pipe(
        map((result) => {
          return result.data?.returnSession || '-4';
        })
      );
  }
}
